import type { LocationData } from "@/data/locations";
import type { TownData } from "@/data/towns";
import type { ServiceConfig, ServiceSlug } from "@/data/serviceLocations";

/** Shared planning guidance, not a claim of work performed in a particular town. */
export const servicePlanning: Record<ServiceSlug, string> = {
  "dog-fence": "Compare fence height and ground-line gaps with your dog's size, jumping and digging habits. Check gates and changes in slope as carefully as the straight runs.",
  "deer-fence": "Plan a continuous enclosure around the planting area. Height, gate closure and gaps at changes in terrain all matter; no fence is a guarantee against every animal.",
  "garden-fence": "Identify which animals are browsing or digging before choosing mesh and ground-line details. Leave practical access for tools, wheelbarrows and seasonal maintenance.",
  "fence-restoration": "Inspect posts, mesh, tension and gate hardware separately. The condition of the supporting structure helps determine whether repair or replacement is appropriate.",
};

function firstSentence(value: string): string {
  return value.split(/(?<=[.!?])\s+/)[0] ?? value;
}

export function stateGeoDescription(name: string): string {
  return `Physical, non-electric dog, deer and garden fencing in ${name}. Explore regional conditions and town guides; installation depends on project scope and state requirements.`;
}

export function townGeoMetadata(town: TownData, region: LocationData, service?: ServiceConfig) {
  const subject = service?.shortName ?? "Dog, Deer & Garden Fencing";
  return {
    title: `${subject} in ${town.name}, ${town.state} | Modern Fence Install`,
    description: `${service?.fullName ?? "Physical, non-electric fencing"} in ${town.name}, ${town.state}. ${firstSentence(town.lotProfile)} Explore ${region.city} planning guidance.`,
  };
}

export function regionGeoMetadata(region: LocationData, service?: ServiceConfig) {
  return {
    title: service ? `${service.shortName} in ${region.city}, ${region.state} | Modern Fence Install` : region.metaTitle,
    description: service
      ? `${service.fullName} in ${region.city}, ${region.state}. ${firstSentence(region.lotNotes)} Compare layout, access and maintenance needs.`
      : region.metaDescription,
  };
}